"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  selectSelectedBranchId,
  useBranchStore,
} from "../../context/useBranchContext";
import { selectAuthToken, useAuthStore } from "../../store/useAuth";
import { handleAuthError } from "../../utils/auth-error-handler";
import { getAuthHeaders, isPublicEndpoint } from "../../utils/auth-utils";
import { buildApiUrl, MutationConfig } from "./hook-utils";

export const useDelete = <T>(endpoint: string, config?: MutationConfig) => {
  const token = useAuthStore(selectAuthToken);
  const queryClient = useQueryClient();
  const branchId = useBranchStore(selectSelectedBranchId) || "";

  const mutation = useMutation<T | null, Error, string | number>({
    mutationFn: async (id?: string | number): Promise<T | null> => {
      try {
        const finalEndpoint = id
          ? `${endpoint}/${id}${branchId ? `?branchId=${branchId}` : ""}`
          : `${endpoint}${branchId ? `?branchId=${branchId}` : ""}`;
        const url = buildApiUrl(finalEndpoint);

        if (!url) {
          throw new Error("URL не доступен");
        }

        if (!token && !isPublicEndpoint(finalEndpoint)) {
          throw new Error("Authentication token is required");
        }

        const response = await fetch(url, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(finalEndpoint, token),
          },
        });

        if (!response.ok) {
          // Try to parse error response from backend
          try {
            const errorText = await response.text();
            if (errorText) {
              const errorData = JSON.parse(errorText);
              if (
                "success" in errorData &&
                !errorData.success &&
                "message" in errorData
              ) {
                const error = new Error(errorData.message);
                (error as any).status = response.status;
                (error as any).data = errorData;
                throw error;
              }
            }
          } catch (parseError) {
            // If it's our custom error, re-throw it
            if (
              parseError instanceof Error &&
              parseError.message !== `HTTP error! status: ${response.status}`
            ) {
              throw parseError;
            }
            // Otherwise, fall through to generic error
          }

          const error = new Error(`HTTP error! status: ${response.status}`);
          (error as any).status = response.status;
          throw error;
        }

        const json = await response.json();

        if ("success" in json && !json.success && "message" in json) {
          const error = new Error(json.message);
          (error as any).status = response.status;
          (error as any).data = json;
          throw error;
        }

        return json;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [endpoint] });
      config?.onSuccess?.(data);
    },
    onError: (error, variables, context) => {
      // Check if this is an auth error and handle logout
      handleAuthError(error);

      // Ensure error is properly structured before passing to custom handler
      if (config?.onError) {
        config.onError(error);
        return;
      }
      const message = (error as any)?.message || "Не удалось удалить данные";
      toast.error(message);
    },
    ...(config?.onMutate && { onMutate: config.onMutate }),
  });

  return mutation;
};
